/**
 * Formulario de opciones SUGERIDO: tipos, guardas y el formato del mensaje de
 * respuesta. Lo usa el chat principal (`components/chat/`); el widget embebido NO
 * lo pinta, por decision (el backend ni siquiera extrae el formulario en runs de
 * widget).
 *
 * El backend deriva las preguntas cerradas de la PROSA del turno
 * (`back/src/preguntas_sugeridas.py`) y las manda aparte, atadas al mensaje por
 * `message_id`. El modelo no llama a ninguna tool para esto. Algunos formularios
 * los construye en cambio una tool a partir de DATOS (los servicios relacionados
 * de `presupuesto`): viajan por el mismo contrato y pueden traer opciones
 * premarcadas (`Opcion.marcada`) y preguntas que admiten no marcar nada
 * (`Pregunta.permite_vacia`).
 *
 * La respuesta viaja como un mensaje humano normal: "Cabecera: opción. Además:
 * texto". Es la misma forma que el modelo ve cuando el usuario contesta en prosa.
 */

/**
 * Clave dentro de `metadata.custom` del mensaje del asistente (assistant-ui) en
 * la que viaja el formulario. La escriben los dos productores —el stream en vivo
 * (`components/chat/sse.ts`) y la carga del historial (`lib/api/chatApi.ts`)— y
 * la lee un unico componente. Vive AQUI porque `chatApi` no debe importar de la
 * capa de componentes para conseguir una constante.
 */
export const METADATA_PREGUNTAS_SUGERIDAS = "preguntasSugeridas";

/**
 * `metadata` del mensaje del asistente con el formulario dentro. Unico
 * constructor de esa forma: lo usan los dos productores, asi que escriben lo
 * mismo por construccion y no por disciplina.
 */
export function metadataSugeridas(s: PreguntasSugeridas): {
  custom: { [METADATA_PREGUNTAS_SUGERIDAS]: PreguntasSugeridas };
} {
  return { custom: { [METADATA_PREGUNTAS_SUGERIDAS]: s } };
}

/**
 * Clave dentro de `metadata.custom` con el modelo que genero el mensaje (para
 * el badge). Vive junto a la del formulario porque las dos comparten el MISMO
 * `custom`: el ultimo mensaje del asistente puede llevar las dos a la vez, y
 * escribir una pisando la otra dejaria un badge o un formulario desaparecido
 * segun el orden en que se construyan.
 */
export const METADATA_MODELO = "modelo";

/** `metadata` del mensaje con el modelo dentro. Unico constructor de esa forma. */
export function metadataModelo(modelo: string): {
  custom: { [METADATA_MODELO]: string };
} {
  return { custom: { [METADATA_MODELO]: modelo } };
}

/**
 * Modelo con el que corrio UN mensaje, leido de `response_metadata.model_name`.
 *
 * `langchain_openai` lo escribe tanto en la respuesta completa como en los
 * chunks del streaming, y llega al historial via el `model_dump` con el que
 * `GET /threads/{id}/state` sirve el state — por eso el badge sobrevive a
 * recargar la pagina. Pero el merge de chunks lo ESTROPEA (ver
 * `desduplicarModelName`), asi que se normaliza aqui: es el constructor unico
 * que usan los dos productores (stream en vivo e historial), y arreglarlo aqui
 * arregla los dos a la vez. Sin el valor devuelve `null` y no se pinta nada:
 * inventarse el modelo del thread para un mensaje viejo seria AFIRMAR algo que
 * no consta.
 *
 * Vive aqui y no en el componente porque lo usa `lib/api/chatApi.ts`, que no
 * debe importar de la capa de componentes.
 */
export function modeloDeMensaje(msg: unknown): string | null {
  if (!msg || typeof msg !== "object") return null;
  const rm = (msg as { response_metadata?: unknown }).response_metadata;
  if (!rm || typeof rm !== "object") return null;
  const valor = (rm as { model_name?: unknown }).model_name;
  if (typeof valor !== "string" || !valor.trim()) return null;
  return desduplicarModelName(valor.trim());
}

/**
 * Deshace la repeticion que el merge de chunks hace del `model_name`.
 *
 * `langchain_core` funde `response_metadata` de los chunks con `merge_dicts`,
 * que para dos strings CONCATENA. Via OpenRouter mas de un chunk trae
 * `model_name`, asi que el mensaje PERSISTIDO lleva el nombre repetido N veces
 * (`qwen/qwen3.8-flashqwen/qwen3.8-flash`, verificado en el state de threads
 * reales) y el badge tras F5 lo mostraba doblado. No se puede arreglar en el
 * origen (es la libreria); el backend hace lo mismo en `_desduplicar_model_name`
 * para tarificar.
 *
 * Se busca el MENOR periodo `p` tal que el nombre sea `nombre.slice(0, p)`
 * repetido un numero entero de veces; cubre 2, 3 y N repeticiones. Un nombre
 * que no sea repeticion EXACTA sale intacto.
 */
export function desduplicarModelName(nombre: string): string {
  const n = nombre.length;
  for (let p = 1; p <= n / 2; p++) {
    if (n % p === 0 && nombre.slice(0, p).repeat(n / p) === nombre) {
      return nombre.slice(0, p);
    }
  }
  return nombre;
}

/**
 * Funde dos `metadata` de mensaje conservando TODAS las claves de `custom`.
 *
 * El formulario sugerido y el modelo viajan en el mismo `custom`, y el ultimo
 * mensaje del asistente puede llevar los dos. Un `{...msg, metadata: X}` a secas
 * borraria el otro sin que nada fallara.
 */
export function fundirMetadata(
  a: { custom?: Record<string, unknown> } | undefined,
  b: { custom?: Record<string, unknown> },
): { custom: Record<string, unknown> } {
  return { custom: { ...(a?.custom ?? {}), ...(b.custom ?? {}) } };
}

/**
 * Modo del formulario segun la posicion del mensaje en el thread. `null` = no
 * pintarlo: mientras el run corre el formulario montaria en lectura y
 * parpadearia como "Cerrado" hasta `end`. Activo solo en el ULTIMO mensaje: si
 * la conversacion siguio, el usuario ya contesto (o lo ignoro) y queda cerrado.
 */
export function modoFormulario(s: { isLast: boolean; isRunning: boolean }): "activo" | "lectura" | null {
  if (s.isRunning) return null;
  return s.isLast ? "activo" : "lectura";
}

export interface Opcion {
  etiqueta: string;
  descripcion?: string | null;
  vista_previa?: string | null;
  recomendada?: boolean;
  /**
   * Marcada al abrir el formulario (premarcado de un formulario construido por el
   * backend a partir de datos, no de la prosa: p. ej. los servicios relacionados
   * del presupuesto, premarcados por historial). Es solo el estado INICIAL: el
   * usuario la desmarca como cualquier otra.
   */
  marcada?: boolean;
}

export interface Pregunta {
  id: string;
  cabecera: string;
  pregunta: string;
  multiple?: boolean;
  /**
   * No marcar nada es una respuesta válida ("ninguno"). Por eso una pregunta así
   * puede traer UNA sola opción: la alternativa es no marcarla. Espejo de
   * `validar_formulario_de_tool` en el backend (mínimo 1 opción con
   * `permite_vacia`, 2 sin él).
   *
   * Esta marca y `Opcion.marcada` se leen ESTRICTAS (`=== true`): un payload que
   * las traiga con otro tipo ("true", 1) no cumple el contrato y se trata como si
   * no las trajera. Leerlas por truthiness convertiria un dato mal formado en una
   * pregunta que nunca queda pendiente.
   */
  permite_vacia?: boolean;
  opciones: Opcion[];
}

/**
 * Forma persistida y streameada del formulario sugerido: el contrato del evento
 * SSE `preguntas_sugeridas` y de `suggested_questions` en
 * `GET /threads/{id}/state`. `message_id` es el id del AIMessage final del turno
 * al que pertenecen las preguntas.
 */
export interface PreguntasSugeridas {
  message_id: string;
  preguntas: Pregunta[];
}

export interface Respuesta {
  seleccion: string[];
  otra: string;
}

export type Respuestas = Record<string, Respuesta>;

function esOpcion(x: unknown): x is Opcion {
  return (
    typeof x === "object" &&
    x !== null &&
    typeof (x as Opcion).etiqueta === "string" &&
    (x as Opcion).etiqueta.length > 0
  );
}

/**
 * Guarda de la lista de preguntas. Exige `id` no vacio y UNICO en la lista: las
 * respuestas se indexan por `id`. Minimo dos opciones, o UNA si la pregunta
 * admite no marcar nada (`permite_vacia`): ahi elegir es marcarla o no.
 */
export function esListaDePreguntas(x: unknown): x is Pregunta[] {
  if (!Array.isArray(x) || x.length === 0) return false;
  const ids = new Set<string>();
  return x.every((p) => {
    if (typeof p !== "object" || p === null) return false;
    const q = p as Partial<Pregunta>;
    if (typeof q.id !== "string" || q.id.length === 0 || ids.has(q.id)) return false;
    ids.add(q.id);
    return (
      typeof q.cabecera === "string" &&
      typeof q.pregunta === "string" &&
      Array.isArray(q.opciones) &&
      q.opciones.length >= (q.permite_vacia === true ? 1 : 2) &&
      q.opciones.every(esOpcion)
    );
  });
}

/** Guarda del contrato `{message_id, preguntas}`. Una lista vacia NO es un formulario. */
export function esPreguntasSugeridas(x: unknown): x is PreguntasSugeridas {
  if (typeof x !== "object" || x === null) return false;
  const s = x as Partial<PreguntasSugeridas>;
  return (
    typeof s.message_id === "string" &&
    s.message_id.length > 0 &&
    esListaDePreguntas(s.preguntas)
  );
}

/**
 * Normaliza un payload del backend (evento SSE o `suggested_questions`) a la forma
 * que se guarda en `metadata.custom`: solo `message_id` y `preguntas`, sin
 * arrastrar campos extra que el backend pudiera anadir. `null` si no cumple el
 * contrato — el llamante entonces no pinta formulario.
 */
export function preguntasSugeridasDe(payload: unknown): PreguntasSugeridas | null {
  if (!esPreguntasSugeridas(payload)) return null;
  return { message_id: payload.message_id, preguntas: payload.preguntas };
}

/**
 * ¿Cuenta la pregunta como respondida? Con `p.permite_vacia` SIEMPRE: no marcar
 * nada es la respuesta "ninguno", no una pregunta pendiente. Sin la pregunta (o
 * sin esa marca), hace falta una opcion o texto libre.
 */
export function respuestaCompleta(r: Respuesta | undefined, p?: Pregunta): boolean {
  if (p?.permite_vacia === true) return true;
  return !!r && (r.seleccion.length > 0 || r.otra.trim().length > 0);
}

/**
 * Estado inicial del formulario: lo que el backend dejó marcado. Una pregunta sin
 * ninguna opcion marcada no tiene entrada (igual que antes de existir el
 * premarcado), asi que un formulario derivado de la prosa arranca vacio.
 */
export function respuestasIniciales(preguntas: Pregunta[]): Respuestas {
  const r: Respuestas = {};
  for (const p of preguntas) {
    const seleccion = p.opciones.filter((o) => o.marcada === true).map((o) => o.etiqueta);
    if (seleccion.length > 0) r[p.id] = { seleccion, otra: "" };
  }
  return r;
}

/** Colapsa saltos de línea y espacios repetidos: la respuesta es una línea por pregunta. */
function unaLinea(texto: string): string {
  return texto.replace(/\s+/g, " ").trim();
}

/**
 * Mensaje de respuesta: una línea por pregunta, en el orden de `preguntas`, con la
 * forma "Cabecera: sel. Además: otra". Las preguntas sin respuesta se omiten (nada
 * de "Cabecera: " colgando) y el texto libre y las etiquetas se colapsan a una
 * línea, venga de donde venga el control que los capturó.
 *
 * Una pregunta `permite_vacia` sin nada marcado NO se omite: sale como
 * "Cabecera: <etiquetaNinguna>". Omitirla dejaria al agente sin saber si el
 * usuario descarto el grupo o si el formulario no lo pregunto. Puede no tener
 * entrada en `respuestas` (nada premarcado y nada tocado), de ahi el `vacia`.
 * `etiquetaNinguna` es obligatoria: es texto visible y viene de i18n, asi que un
 * default en castellano en este modulo acabaria en el mensaje sin traducir.
 */
export function formatearRespuestas(
  preguntas: Pregunta[],
  respuestas: Respuestas,
  etiquetaAdemas: string,
  etiquetaNinguna: string,
): string {
  return preguntas
    .filter((p) => respuestaCompleta(respuestas[p.id], p))
    .map((p) => {
      const r = respuestas[p.id] ?? { seleccion: [], otra: "" };
      const sel = r.seleccion.map(unaLinea).join(", ");
      const otra = unaLinea(r.otra);
      if (sel && otra) return `${p.cabecera}: ${sel}. ${etiquetaAdemas}: ${otra}`;
      return `${p.cabecera}: ${sel || otra || etiquetaNinguna}`;
    })
    .join("\n");
}
